export class MonthDate extends Date {
    /**
     * Create a MonthDate object for a specific year and month.
     * @param {number} year - The full year (e.g., 2023)
     * @param {number} month - The month (1-based, 1 = January, 12 = December)
     */
    constructor(year: number, month: number) {
        super(year, month - 1);
    }

    /**
     * Parse a string in the format "YYYY-MM" into a MonthDate object.
     * If the input string is not in the correct format, returns null.
     * @param {string} dateString - The string to parse
     * @returns {MonthDate|null} A MonthDate object representing the parsed date, or null if the input is invalid
     */
    static parseYearMonth(dateString: string): MonthDate | null {
        const parts = dateString.split("-");
        if (parts.length !== 2) {
            return null;
        }

        const [year, month] = parts.map((value) => parseInt(value, 10));
        if (Number.isNaN(year) || Number.isNaN(month)) {
            return null;
        }
        if (month < 1 || month > 12) {
            return null;
        }

        return new MonthDate(year, month);
    }

    /**
     * Format the month of this date as a three-letter abbreviation
     * (e.g. "Jan", "Feb", etc.)
     * @returns {string} The formatted month abbreviation
     */
    formatMonth(): string {
        const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];
        return monthNames[this.getMonth()];
    }

    /**
     * Return a string representation of the date in the format "YYYY-MM".
     * Example: "2023-07" for July 2023.
     * @returns {string} The formatted date string
     */
    toString(): string {
        const year = this.getFullYear();
        const month = (this.getMonth() + 1).toString().padStart(2, "0");
        return `${year}-${month}`;
    }

    /**
     * Find the candidate MonthDate closest to today's month.
     * Distance is measured in whole months. If `candidates` is empty
     * the function returns `null`.
     * Tie-breaker: when two candidates are equally distant, prefer
     * the one that is later (greater) in time.
     *
     * @param {MonthDate[]} candidates - array of MonthDate to compare
     * @returns {MonthDate | null} the closest MonthDate or null
     */
    static getClosestToToday(candidates: MonthDate[]): MonthDate | null {
        const today = new Date();
        if (!Array.isArray(candidates) || candidates.length === 0) {
            return null;
        }

        const todayYear = today.getFullYear();
        const todayMonth = today.getMonth(); // 0-based

        const monthDistance = (d: Date) =>
            (d.getFullYear() - todayYear) * 12 + (d.getMonth() - todayMonth);

        let best: MonthDate | null = null;
        let bestAbsMonths = Infinity;

        for (const c of candidates) {
            if (!(c instanceof Date)) {
                continue;
            }
            const diff = monthDistance(c);
            const absDiff = Math.abs(diff);

            if (absDiff < bestAbsMonths) {
                best = c;
                bestAbsMonths = absDiff;
            } else if (absDiff === bestAbsMonths && best !== null) {
                // tie-breaker: prefer the later date
                if (c.getTime() > best.getTime()) {
                    best = c;
                }
            }
        }

        return best;
    }
}